import { api } from './api.js';
import { ENDPOINTS } from '../config/endpoints.js';
import { storage } from '../config/firebaseConfig.js';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export const medicalRecordService = {
  /* ── Pet Photo ────────────────────────────────────────────── */
  async uploadPetPhoto(file, petId, entityId) {
    try {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return { success: false, error: 'Formato inválido. Use JPG, PNG ou WEBP.' };
      }
      if (file.size > 5 * 1024 * 1024) {
        return { success: false, error: 'Arquivo muito grande. Máximo 5MB.' };
      }

      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const storageRef = ref(storage, `pets/${petId}/${fileName}`);
      await uploadBytes(storageRef, file);
      const photoUrl = await getDownloadURL(storageRef);

      const response = await api.put(`${ENDPOINTS.V3_MEDICAL_RECORD_PETS}/${petId}`, { photo: photoUrl, ...(entityId ? { entityId } : {}) });
      return { success: true, photoUrl, pet: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao enviar foto' };
    }
  },
  /* ── Owners ───────────────────────────────────────────────── */
  async getOwners({ entityId, search = '', page = 1, limit = 20 } = {}) {
    try {
      let url = `${ENDPOINTS.V3_MEDICAL_RECORD_OWNERS}?entityId=${entityId}&page=${page}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await api.get(url);
      const res = response.data?.response || {};
      return { success: true, owners: res.owners || [], total: res.total || 0, page: res.page || 1 };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar tutores' };
    }
  },

  async getOwner(id, entityId) {
    try {
      const url = entityId
        ? `${ENDPOINTS.V3_MEDICAL_RECORD_OWNERS}/${id}?entityId=${entityId}`
        : `${ENDPOINTS.V3_MEDICAL_RECORD_OWNERS}/${id}`;
      const response = await api.get(url);
      return { success: true, owner: response.data?.response || null };
    } catch (error) {
      console.error('Error fetching owner:', error);
      throw error;
    }
  },

  async createOwner(payload) {
    try {
      const response = await api.post(ENDPOINTS.V3_MEDICAL_RECORD_OWNERS, payload);
      return { success: true, owner: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao criar tutor' };
    }
  },

  async updateOwner(id, payload) {
    try {
      const response = await api.put(`${ENDPOINTS.V3_MEDICAL_RECORD_OWNERS}/${id}`, payload);
      return { success: true, owner: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao atualizar tutor' };
    }
  },

  async deleteOwner(id) {
    try {
      await api.delete(`${ENDPOINTS.V3_MEDICAL_RECORD_OWNERS}/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao deletar tutor' };
    }
  },

  async deletePet(id) {
    try {
      await api.delete(`${ENDPOINTS.V3_MEDICAL_RECORD_PETS}/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao deletar pet' };
    }
  },

  /* ── Pets ─────────────────────────────────────────────────── */
  async getPets({ ownerId, entityId, search, species, inactive, page, limit } = {}) {
    try {
      const params = new URLSearchParams();
      if (ownerId) params.append('ownerId', ownerId);
      if (entityId) params.append('entityId', entityId);
      if (search) params.append('search', search);
      if (species) params.append('species', species);
      if (inactive !== undefined) params.append('inactive', inactive);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      const response = await api.get(`${ENDPOINTS.V3_MEDICAL_RECORD_PETS}?${params.toString()}`);
      const res = response.data?.response;
      return { success: true, pets: Array.isArray(res) ? res : (res?.pets || []), total: res?.total ?? null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar pets' };
    }
  },

  async getPet(id) {
    try {
      const response = await api.get(`${ENDPOINTS.V3_MEDICAL_RECORD_PETS}/${id}`);
      return { success: true, pet: response.data?.response || null };
    } catch (error) {
      console.error('Error fetching pet:', error);
      throw error;
    }
  },

  async createPet(payload) {
    try {
      const response = await api.post(ENDPOINTS.V3_MEDICAL_RECORD_PETS, payload);
      return { success: true, pet: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao criar pet' };
    }
  },

  async updatePet(id, payload) {
    try {
      const response = await api.put(`${ENDPOINTS.V3_MEDICAL_RECORD_PETS}/${id}`, payload);
      return { success: true, pet: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao atualizar pet' };
    }
  },

  /* ── Consultations ────────────────────────────────────────── */
  async getConsultations({ petId, scheduleId, page = 1, limit = 20 } = {}) {
    try {
      let url = `${ENDPOINTS.V3_MEDICAL_RECORD_CONSULTATIONS}?petId=${petId}&page=${page}&limit=${limit}`;
      if (scheduleId) url += `&scheduleId=${scheduleId}`;
      const response = await api.get(url);
      const res = response.data?.response || {};
      const list = Array.isArray(res) ? res : (res.consultations || []);
      return { success: true, consultations: list, total: res.total || list.length };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar consultas' };
    }
  },

  async getConsultation(id) {
    try {
      const response = await api.get(`${ENDPOINTS.V3_MEDICAL_RECORD_CONSULTATIONS}/${id}`);
      return { success: true, consultation: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar consulta' };
    }
  },

  async createConsultation(payload) {
    try {
      const response = await api.post(ENDPOINTS.V3_MEDICAL_RECORD_CONSULTATIONS, payload);
      return { success: true, consultation: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao criar consulta' };
    }
  },

  async updateConsultation(id, payload) {
    try {
      const response = await api.put(`${ENDPOINTS.V3_MEDICAL_RECORD_CONSULTATIONS}/${id}`, payload);
      return { success: true, consultation: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao atualizar consulta' };
    }
  },

  async deleteConsultation(id) {
    try {
      await api.delete(`${ENDPOINTS.V3_MEDICAL_RECORD_CONSULTATIONS}/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao remover consulta' };
    }
  },

  /* ── Medications ──────────────────────────────────────────── */
  async saveMedication(consultationId, payload) {
    try {
      const response = await api.post(`${ENDPOINTS.V3_MEDICAL_RECORD_MEDICATIONS}/${consultationId}/medications`, payload);
      return { success: true, medication: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao salvar medicamento' };
    }
  },

  async updateMedication(consultationId, medicationId, payload) {
    try {
      const response = await api.put(`${ENDPOINTS.V3_MEDICAL_RECORD_MEDICATIONS}/${consultationId}/medications/${medicationId}`, payload);
      return { success: true, medication: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao atualizar medicamento' };
    }
  },

  async deleteMedication(consultationId, medicationId) {
    try {
      await api.delete(`${ENDPOINTS.V3_MEDICAL_RECORD_MEDICATIONS}/${consultationId}/medications/${medicationId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao excluir medicamento' };
    }
  },

  /* ── Timeline ─────────────────────────────────────────────── */
  async getTimeline({ petId, types = '', page = 1, limit = 30 } = {}) {
    try {
      let url = `${ENDPOINTS.V3_MEDICAL_RECORD_TIMELINE}?petId=${petId}&page=${page}&limit=${limit}`;
      if (types) url += `&types=${types}`;
      const response = await api.get(url);
      const res = response.data?.response || {};
      const events = Array.isArray(res) ? res : (res.events || res.timeline || []);
      return { success: true, events, total: res.total || events.length };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar timeline' };
    }
  },

  /* ── Documents ────────────────────────────────────────────── */
  async getDocuments({ petId, consultationId } = {}) {
    try {
      let url = `${ENDPOINTS.V3_MEDICAL_RECORD_DOCUMENTS}?`;
      if (petId) url += `petId=${petId}&`;
      if (consultationId) url += `consultationId=${consultationId}`;
      const response = await api.get(url);
      const res = response.data?.response;
      return { success: true, documents: Array.isArray(res) ? res : (res?.documents || []) };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao buscar documentos' };
    }
  },

  async uploadDocument(file, { petId, entityId, title, type, consultationId, visibleToTutor = false } = {}) {
    try {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        return { success: false, error: 'Formato inválido. Use PDF, DOC, DOCX, JPG, PNG ou WEBP.' };
      }
      if (file.size > 20 * 1024 * 1024) {
        return { success: false, error: 'Arquivo muito grande. Máximo 20MB.' };
      }

      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const storageRef = ref(storage, `documents/${petId}/${fileName}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      const payload = {
        fileUrl,
        fileName: file.name,
        title: title || file.name,
        type,
        petId,
        entityId,
        visibleToTutor,
        ...(consultationId ? { consultationId } : {}),
      };
      const response = await api.post(ENDPOINTS.V3_MEDICAL_RECORD_DOCUMENTS, payload);
      return { success: true, document: response.data?.response || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao enviar documento' };
    }
  },

  async deleteDocument(id) {
    try {
      await api.delete(`${ENDPOINTS.V3_MEDICAL_RECORD_DOCUMENTS}/${id}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao excluir documento' };
    }
  },
};
